import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Building2, Upload, X, Users, MapPin, Phone, Mail, Globe, 
  Shield, Pencil, Save, XCircle, Plus, Trash2
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import OrganizationManager from "@/components/organization/OrganizationManager";
import { ENTITY_TYPES } from "@shared/entityTypes";
import { getSicCodeLabel } from "@shared/sicCodes";
import { formatDateSafe } from "@/lib/utils";
import { formatEINForDisplay } from "@/lib/ein";
import { ProfilePictureUpload } from "@/components/ui/profile-picture-upload";

const CONTACT_TYPES = [
  { value: "hr", label: "HR" },
  { value: "accounting", label: "Accounting" },
  { value: "benefits", label: "Benefits" },
  { value: "payroll", label: "Payroll" },
  { value: "general", label: "General" },
  { value: "executive", label: "Executive" },
];

export default function CompanyProfile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const clientId = user?.clientId;

  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContactUserId, setNewContactUserId] = useState("");
  const [newContactType, setNewContactType] = useState("");

  const { data: clientData, isLoading, error } = useQuery<any>({
    queryKey: ["/api/clients", String(clientId)],
    queryFn: async () => {
      if (!clientId) return null;
      const r = await fetch(`/api/clients/${clientId}`, { credentials: "include" });
      if (!r.ok) {
        throw new Error(`Failed to load company: ${r.status}`);
      }
      return r.json();
    },
    enabled: !!clientId,
  });

  const { data: brokerContacts = [] } = useQuery<any[]>({
    queryKey: ["/api/clients", clientId, "broker-contacts"],
    queryFn: async () => {
      if (!clientId) return [];
      const r = await fetch(`/api/clients/${clientId}/broker-contacts`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!clientId,
  });

  const { data: companyAdmins = [] } = useQuery<any[]>({
    queryKey: ["/api/org/admins", "client", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const r = await fetch(`/api/org/admins?ownerType=client&ownerId=${clientId}`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!clientId,
  });

  const { data: availableEmployees = [] } = useQuery<any[]>({
    queryKey: ["/api/org/available-admins", "client", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const r = await fetch(`/api/org/available-admins?ownerType=client&ownerId=${clientId}`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!clientId && showAddContact,
  });

  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ["/api/employees"],
    queryFn: async () => {
      const r = await fetch("/api/employees", { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!clientId,
  });

  useEffect(() => {
    if (clientData) {
      setFormData({
        name: clientData.name || "",
        ein: clientData.ein || "",
        entityType: clientData.entityType || "",
        address1: clientData.address1 || clientData.address || "",
        address2: clientData.address2 || "",
        city: clientData.city || "",
        state: clientData.state || "",
        zipCode: clientData.zipCode || "",
        county: clientData.county || "",
        primaryContactName: clientData.primaryContactName || "",
        contactEmail: clientData.contactEmail || "",
        contactPhone: clientData.contactPhone || "",
        website: clientData.website || "",
      });
    }
  }, [clientData]);

  const handleLogoChange = async () => {
    // Invalidate queries to refresh client data
    qc.invalidateQueries({ queryKey: ["/api/clients", String(clientId)] });
    qc.invalidateQueries({ queryKey: ["/api/user/profile"] });
  };

  const updateClientMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const r = await apiRequest("PATCH", `/api/clients/${clientId}`, data);
      if (!r.ok) throw new Error("Failed to update company");
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/clients", clientId] });
      qc.invalidateQueries({ queryKey: ["/api/user/profile"] });
      setEditingSection(null);
      toast({ title: "Company information updated", variant: "success" });
    },
    onError: () => {
      toast({ title: "Failed to update company", variant: "destructive" });
    },
  });

  const addContactMutation = useMutation({
    mutationFn: async ({ userId, contactType }: { userId: string; contactType: string }) => {
      const r = await apiRequest("POST", "/api/org/admins", {
        ownerType: "client",
        ownerId: clientId,
        userId,
        contactType,
      });
      if (!r.ok) throw new Error("Failed to add contact");
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/org/admins", "client", clientId] });
      qc.invalidateQueries({ queryKey: ["/api/org/available-admins", "client", clientId] });
      setShowAddContact(false);
      setNewContactUserId("");
      setNewContactType("");
      toast({ title: "Contact added successfully", variant: "success" });
    },
    onError: () => {
      toast({ title: "Failed to add contact", variant: "destructive" });
    },
  });

  const removeContactMutation = useMutation({
    mutationFn: async (userId: string) => {
      const r = await apiRequest("DELETE", `/api/org/admins/${encodeURIComponent(userId)}?ownerType=client&ownerId=${clientId}`);
      if (!r.ok) throw new Error("Failed to remove contact");
      return;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/org/admins", "client", clientId] });
      qc.invalidateQueries({ queryKey: ["/api/org/available-admins", "client", clientId] });
      toast({ title: "Contact removed", variant: "success" });
    },
    onError: () => {
      toast({ title: "Failed to remove contact", variant: "destructive" });
    },
  });



  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = (section: string) => {
    let updateData: Record<string, any> = {};
    
    if (section === "details") {
      updateData = {
        name: formData.name,
        ein: formData.ein,
        entityType: formData.entityType,
      };
    } else if (section === "address") {
      updateData = {
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        county: formData.county,
      };
    } else if (section === "contact") {
      updateData = {
        primaryContactName: formData.primaryContactName,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        website: formData.website,
      };
    }
    
    updateClientMutation.mutate(updateData);
  };

  const handleCancel = () => {
    if (clientData) {
      setFormData({
        name: clientData.name || "",
        ein: clientData.ein || "",
        entityType: clientData.entityType || "",
        address1: clientData.address1 || clientData.address || "",
        address2: clientData.address2 || "",
        city: clientData.city || "",
        state: clientData.state || "",
        zipCode: clientData.zipCode || "",
        county: clientData.county || "",
        primaryContactName: clientData.primaryContactName || "",
        contactEmail: clientData.contactEmail || "",
        contactPhone: clientData.contactPhone || "",
        website: clientData.website || "",
      });
    }
    setEditingSection(null);
  };

  const handleAddContact = () => {
    if (!newContactUserId || !newContactType) {
      toast({ title: "Please select an employee and contact type", variant: "destructive" });
      return;
    }
    addContactMutation.mutate({ userId: newContactUserId, contactType: newContactType });
  };

  const getContactTypeLabel = (type: string | null) => {
    if (!type) return "Admin";
    const found = CONTACT_TYPES.find(ct => ct.value === type);
    return found ? found.label : type;
  };

  if (!clientId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">No company profile available.</p>
      </div>
    );
  }

  const EditButton = ({ section }: { section: string }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => setEditingSection(section)}
      className="h-8 px-2"
    >
      <Pencil className="h-4 w-4" />
    </Button>
  );

  const SaveCancelButtons = ({ section }: { section: string }) => (
    <div className="flex gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={handleCancel}
        disabled={updateClientMutation.isPending}
        className="h-8"
      >
        <XCircle className="h-4 w-4 mr-1" />
        Cancel
      </Button>
      <Button
        size="sm"
        onClick={() => handleSave(section)}
        disabled={updateClientMutation.isPending}
        className="h-8"
      >
        <Save className="h-4 w-4 mr-1" />
        Save
      </Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Company Profile</h1>
        <p className="text-muted-foreground">
          Manage your company information, contacts, and organization structure
        </p>
      </div>

      <Tabs defaultValue="info" className="space-y-4">
        <TabsList>
          <TabsTrigger value="info">Company Info</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="organization">Organization</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Company Logo
                </CardTitle>
                <CardDescription>
                  Your logo appears in the dashboard and on public pages
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-6">
                  <ProfilePictureUpload
                    currentPicture={clientData?.logoUrl ?? clientData?.logo}
                    firstName={clientData?.name || "Company"}
                    lastName=""
                    onPictureChange={handleLogoChange}
                    uploadEndpoint={`/api/clients/${clientId}/logo`}
                    deleteEndpoint={`/api/clients/${clientId}/logo`}
                    fetchUrlEndpoint={`/api/clients/${clientId}/logo-url`}
                    fieldName="logo"
                    size="xl"
                    variant="logo"
                    fallbackIcon={<Building2 className="h-8 w-8" />}
                    showEditOnHover={true}
                  />
                  <div className="flex-1 mt-2">
                    <p className="text-sm text-muted-foreground">
                      Click the logo to upload or remove it. PNG, JPG or GIF. Max 10MB. Recommended size: 200x80px.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Company Details
                </CardTitle>
                {editingSection === "details" ? (
                  <SaveCancelButtons section="details" />
                ) : (
                  <EditButton section="details" />
                )}
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                {editingSection === "details" ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="name">Company Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ein">EIN</Label>
                      <Input
                        id="ein"
                        value={formData.ein}
                        onChange={(e) => handleInputChange("ein", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="entityType">Entity Type</Label>
                      <Select
                        value={formData.entityType || ""}
                        onValueChange={(val) => handleInputChange("entityType", val)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select entity type" />
                        </SelectTrigger>
                        <SelectContent>
                          {ENTITY_TYPES.map((et) => (
                            <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-xs">Company Name</Label>
                      <p className="font-medium">{clientData?.name || "—"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Status</Label>
                      <Badge variant={clientData?.status === "active" ? "default" : "secondary"}>
                        {clientData?.status || "Unknown"}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Active Employees</Label>
                      <p className="font-medium">
                        {employees.filter((emp: any) => 
                          emp.clientId === clientId && 
                          String(emp.status).toLowerCase() === "active"
                        ).length}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">EIN</Label>
                      <p className="font-medium">{clientData?.ein ? formatEINForDisplay(clientData.ein) : "—"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Entity Type</Label>
                      <p className="font-medium">{clientData?.entityType || "—"}</p>
                    </div>
                    {((clientData as any)?.sicCodeId || clientData?.sic) && (
                      <div>
                        <Label className="text-muted-foreground text-xs">SIC</Label>
                        <p className="font-medium">
                          {(clientData as any)?.sicCodeId
                            ? getSicCodeLabel((clientData as any).sicCodeId)
                            : clientData?.sic}
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground text-xs">Client Since</Label>
                      <p className="font-medium">
                        {formatDateSafe((clientData as any)?.clientSince || clientData?.createdAt)}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Address
                </CardTitle>
                {editingSection === "address" ? (
                  <SaveCancelButtons section="address" />
                ) : (
                  <EditButton section="address" />
                )}
              </CardHeader>
              <CardContent className="pt-4">
                {editingSection === "address" ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="address1">Street Address</Label>
                      <Input
                        id="address1"
                        value={formData.address1}
                        onChange={(e) => handleInputChange("address1", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="address2">Address Line 2</Label>
                      <Input
                        id="address2"
                        value={formData.address2}
                        onChange={(e) => handleInputChange("address2", e.target.value)}
                        className="mt-1"
                        placeholder="Suite, Apt, etc."
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => handleInputChange("city", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipCode">ZIP Code</Label>
                      <Input
                        id="zipCode"
                        value={formData.zipCode}
                        onChange={(e) => handleInputChange("zipCode", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="county">County</Label>
                      <Input
                        id="county"
                        value={formData.county}
                        onChange={(e) => handleInputChange("county", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <Label className="text-muted-foreground text-xs">Street Address</Label>
                      <p className="font-medium">
                        {clientData?.address1 || clientData?.address || "—"}
                        {clientData?.address2 && <span className="block">{clientData.address2}</span>}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">City</Label>
                      <p className="font-medium">{clientData?.city || "—"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">State</Label>
                      <p className="font-medium">{clientData?.state || "—"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">ZIP Code</Label>
                      <p className="font-medium">{clientData?.zipCode || "—"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">County</Label>
                      <p className="font-medium">{clientData?.county || "—"}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Contact Information
                </CardTitle>
                {editingSection === "contact" ? (
                  <SaveCancelButtons section="contact" />
                ) : (
                  <EditButton section="contact" />
                )}
              </CardHeader>
              <CardContent className="pt-4">
                {editingSection === "contact" ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="primaryContactName">Primary Contact</Label>
                      <Input
                        id="primaryContactName"
                        value={formData.primaryContactName}
                        onChange={(e) => handleInputChange("primaryContactName", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contactEmail">Email</Label>
                      <Input
                        id="contactEmail"
                        type="email"
                        value={formData.contactEmail}
                        onChange={(e) => handleInputChange("contactEmail", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contactPhone">Phone</Label>
                      <Input
                        id="contactPhone"
                        type="tel"
                        value={formData.contactPhone}
                        onChange={(e) => handleInputChange("contactPhone", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        value={formData.website}
                        onChange={(e) => handleInputChange("website", e.target.value)}
                        className="mt-1"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-xs">Primary Contact</Label>
                      <p className="font-medium">{clientData?.primaryContactName || "—"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Email</Label>
                      <p className="font-medium">{clientData?.contactEmail || "—"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Phone</Label>
                      <p className="font-medium">{clientData?.contactPhone || "—"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Website</Label>
                      <p className="font-medium">{clientData?.website || "—"}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="contacts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Your Broker Team
              </CardTitle>
              <CardDescription>
                Your assigned insurance broker contacts (managed by your broker)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {brokerContacts.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No broker contacts assigned yet.
                </p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {brokerContacts.map((contact: any) => (
                    <Card key={contact.id || contact.role} className="border">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Avatar className="h-12 w-12">
                            {contact.profileImageUrl ? (
                              <AvatarImage src={contact.profileImageUrl} alt={contact.name} />
                            ) : (
                              <AvatarFallback className="bg-blue-100 text-blue-600">
                                {contact.firstName?.[0]}{contact.lastName?.[0]}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {contact.firstName} {contact.lastName}
                            </p>
                            <Badge variant="outline" className="text-xs mt-1">
                              {contact.roleLabel || contact.role || "Broker"}
                            </Badge>
                            {contact.email && (
                              <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                                <Mail className="h-3 w-3" />
                                <span className="truncate">{contact.email}</span>
                              </div>
                            )}
                            {contact.phone && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                <span>{contact.phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Company Contacts
                </CardTitle>
                <CardDescription className="mt-1">
                  Employees designated as company contacts by role
                </CardDescription>
              </div>
              {!showAddContact && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddContact(true)}
                  className="h-8"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Contact
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {showAddContact && (
                <div className="mb-6 p-4 border rounded-lg bg-muted/50">
                  <h4 className="font-medium mb-3">Add New Contact</h4>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label>Employee</Label>
                      <Select value={newContactUserId} onValueChange={setNewContactUserId}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select employee" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableEmployees.map((emp: any) => (
                            <SelectItem key={emp.id} value={emp.id}>
                              {emp.firstName} {emp.lastName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Contact Type</Label>
                      <Select value={newContactType} onValueChange={setNewContactType}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {CONTACT_TYPES.map((ct) => (
                            <SelectItem key={ct.value} value={ct.value}>
                              {ct.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end gap-2">
                      <Button
                        onClick={handleAddContact}
                        disabled={addContactMutation.isPending}
                        size="sm"
                      >
                        Add
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowAddContact(false);
                          setNewContactUserId("");
                          setNewContactType("");
                        }}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {companyAdmins.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No company contacts set. Click "Add Contact" to designate employees as company contacts.
                </p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {companyAdmins.map((admin: any) => (
                    <Card key={admin.id} className="border group relative">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-green-100 text-green-600">
                              {admin.firstName?.[0]}{admin.lastName?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {admin.firstName} {admin.lastName}
                            </p>
                            <Badge variant="outline" className="text-xs mt-1">
                              {getContactTypeLabel(admin.contactType)}
                            </Badge>
                            {admin.isPrimaryAdmin && (
                              <Badge variant="default" className="text-xs mt-1 ml-1">
                                Primary Admin
                              </Badge>
                            )}
                            {admin.email && (
                              <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                                <Mail className="h-3 w-3" />
                                <span className="truncate">{admin.email}</span>
                              </div>
                            )}
                          </div>
                          {!admin.isPrimaryAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 right-2"
                              onClick={() => removeContactMutation.mutate(admin.id)}
                              disabled={removeContactMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="organization" className="space-y-4">
          <OrganizationManager ownerType="client" ownerId={clientId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
